import { useEffect, useState } from "react";
import {
  Modal,
  Stepper,
  TextInput,
  PasswordInput,
  Select,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Text,
  Alert,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconInfoCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { UploadField } from "../../components/UploadField";
import { onboardingApi } from "../../api/onboarding";
import { errorMessage } from "../../lib/axios";
import { parseBackendDate } from "../../lib/format";

const LATRA_TYPES = [
  { value: "car", label: "Car" },
  { value: "motorcycle", label: "Motorcycle / Bicycle" },
  { value: "tricycle", label: "Tricycle (Bajaji)" },
];

function dateToDob(d: Date | null): string {
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

export function DriverOnboardModal({
  opened,
  onClose,
  onDone,
}: {
  opened: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [step, setStep] = useState(0);
  // OTP
  const [phone, setPhone] = useState("");
  const [pinId, setPinId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  // personal
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("male");
  const [dob, setDob] = useState<Date | null>(null);
  // Drivers sign in with phone + password now, so onboarding sets the first one.
  const [password, setPassword] = useState("");
  const [profilePic, setProfilePic] = useState("");
  // vehicle
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [color, setColor] = useState("");
  const [latraType, setLatraType] = useState("car");
  const [vehicleImage, setVehicleImage] = useState("");
  // documents
  const [licenseFront, setLicenseFront] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [licenseCategory, setLicenseCategory] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [insImage, setInsImage] = useState("");
  const [insExpiry, setInsExpiry] = useState<Date | null>(null);
  const [d4Image, setD4Image] = useState("");
  const [d4Expiry, setD4Expiry] = useState<Date | null>(null);

  const [terms, setTerms] = useState<string | undefined>();

  useEffect(() => {
    if (opened) {
      // reset
      setStep(0);
      setPhone("");
      setPinId("");
      setOtp("");
      setOtpSent(false);
      setOtpVerified(false);
      setName("");
      setEmail("");
      setGender("male");
      setDob(null);
      setPassword("");
      setProfilePic("");
      setMake("");
      setModel("");
      setColor("");
      setLatraType("car");
      setVehicleImage("");
      setLicenseFront("");
      setLicenseNumber("");
      setLicenseCategory("");
      setRegNumber("");
      setInsImage("");
      setInsExpiry(null);
      setD4Image("");
      setD4Expiry(null);
      onboardingApi.termsVersion().then(setTerms).catch(() => setTerms(undefined));
    }
  }, [opened]);

  const sendOtp = async () => {
    setBusy(true);
    try {
      const r = await onboardingApi.sendOtp(phone.trim());
      setPinId(r?.pinId ?? "");
      setOtpSent(true);
      notifications.show({ color: "green", message: "OTP sent to the driver's phone" });
    } catch (e) {
      notifications.show({ color: "red", message: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      const r = await onboardingApi.verifyOtp(phone.trim(), otp.trim(), pinId);
      // A real accessToken means the driver already exists.
      if (r?.accessToken) {
        notifications.show({
          color: "red",
          message: "A driver with this phone is already registered.",
        });
        return;
      }
      setOtpVerified(true);
      setStep(1);
      notifications.show({ color: "green", message: "Phone verified" });
    } catch (e) {
      notifications.show({ color: "red", message: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const detailsValid =
    name.trim() &&
    /\S+@\S+\.\S+/.test(email) &&
    dob &&
    password.length >= 6 &&
    profilePic &&
    make.trim() &&
    model.trim() &&
    color.trim() &&
    vehicleImage;

  const submit = async () => {
    setBusy(true);
    try {
      if (!terms) throw new Error("Terms & conditions version unavailable — try again.");
      const reg = await onboardingApi.register({
        personalDetails: {
          name: name.trim(),
          email: email.trim(),
          phoneNumber: phone.trim(),
          gender,
          dob: dateToDob(dob),
          profilePicUrl: profilePic,
        },
        password,
        vehicleDetails: {
          make: make.trim(),
          model: model.trim(),
          color: color.trim(),
          latraVehicleType: latraType,
          vehicleImages: [vehicleImage],
          ...(regNumber.trim() ? { registrationNumber: regNumber.trim() } : {}),
        },
        languagePreference: "en",
        termsAndConditionAcceptVersion: terms,
      });

      // Documents may be sent partially — the backend merges them into what is
      // already stored and only promotes the driver to UNDER_APPROVAL once the
      // required set (licence, LATRA D4, insurance) is complete. The licence
      // expiry is not asked for here: an admin sets it at approval.
      const licenseReady =
        licenseNumber.trim() && licenseCategory.trim() && licenseFront;
      const d4Ready = d4Image && d4Expiry;
      const insReady = insImage && insExpiry;

      const requiredComplete = licenseReady && d4Ready && insReady;
      const anyDocument = licenseReady || d4Ready || insReady;

      // Vehicle registration number is sent above, in vehicleDetails — it's a
      // vehicle attribute, not a document, and has no slot under `documents`.
      if (anyDocument && reg?.tokens?.accessToken) {
        await onboardingApi.submitDocuments(reg.tokens.accessToken, {
          ...(licenseReady && {
            licenseDetails: {
              licenseNumber: licenseNumber.trim(),
              licenseCategory: licenseCategory.trim(),
              frontImageUrl: licenseFront,
            },
          }),
          ...(d4Ready && {
            latraVehicleLicenseD4Details: {
              imageUrl: d4Image,
              expiryDate: dateToDob(d4Expiry),
            },
          }),
          ...(insReady && {
            vehicleInsuranceDetails: {
              imageUrl: insImage,
              expiryDate: dateToDob(insExpiry),
            },
          }),
        });
      }

      if (requiredComplete) {
        notifications.show({
          color: "green",
          message: "Driver onboarded — awaiting approval.",
        });
      } else {
        notifications.show({
          color: "yellow",
          message:
            "Driver created. Licence, LATRA D4 and insurance are still needed " +
            "before they can be sent for approval.",
        });
      }
      onDone();
      onClose();
    } catch (e) {
      notifications.show({ color: "red", message: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Onboard driver" centered size="xl">
      <Stepper active={step} onStepClick={setStep} size="sm">
        {/* Step 1 — phone + OTP */}
        <Stepper.Step label="Verify phone" allowStepSelect={false}>
          <Stack mt="md">
            <Alert icon={<IconInfoCircle size={16} />} color="blue">
              Send an OTP to the driver's phone (they're in the office) and enter the
              code they receive to verify the number.
            </Alert>
            <Group align="flex-end">
              <TextInput
                label="Driver phone"
                placeholder="+255…"
                value={phone}
                onChange={(e) => setPhone(e.currentTarget.value)}
                style={{ flex: 1 }}
                disabled={otpVerified}
              />
              <Button onClick={sendOtp} loading={busy} disabled={!phone.trim() || otpVerified}>
                {otpSent ? "Resend OTP" : "Send OTP"}
              </Button>
            </Group>
            {otpSent && !otpVerified && (
              <Group align="flex-end">
                <TextInput
                  label="OTP code"
                  placeholder="6 digits"
                  value={otp}
                  onChange={(e) => setOtp(e.currentTarget.value)}
                  style={{ flex: 1 }}
                />
                <Button onClick={verifyOtp} loading={busy} disabled={otp.trim().length !== 6}>
                  Verify
                </Button>
              </Group>
            )}
            {otpVerified && (
              <Text c="var(--success-fg)" size="sm" fw={600}>
                ✓ Phone verified — continue to details.
              </Text>
            )}
          </Stack>
        </Stepper.Step>

        {/* Step 2 — personal + vehicle */}
        <Stepper.Step label="Details" allowStepSelect={false}>
          <Stack mt="md">
            <Text size="sm" fw={700} c="dimmed">Personal</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} required />
              <TextInput label="Email" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} required />
              <Select label="Gender" data={[{ value: "male", label: "Male" }, { value: "female", label: "Female" }]} value={gender} onChange={(v) => setGender(v ?? "male")} allowDeselect={false} />
              <DateInput label="Date of birth" value={dob} onChange={(d) => setDob(parseBackendDate(d as Date | string | null))} maxDate={new Date()} required />
              <PasswordInput label="Sign-in password" description="The driver uses this with their phone number to sign in." value={password} onChange={(e) => setPassword(e.currentTarget.value)} required />
              <UploadField label="Profile photo" value={profilePic} onChange={setProfilePic} />
            </SimpleGrid>
            <Text size="sm" fw={700} c="dimmed" mt="sm">Vehicle</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Select label="Vehicle type" data={LATRA_TYPES} value={latraType} onChange={(v) => setLatraType(v ?? "car")} allowDeselect={false} />
              <TextInput label="Make" value={make} onChange={(e) => setMake(e.currentTarget.value)} required />
              <TextInput label="Model" value={model} onChange={(e) => setModel(e.currentTarget.value)} required />
              <TextInput label="Color" value={color} onChange={(e) => setColor(e.currentTarget.value)} required />
              <UploadField label="Vehicle photo (optional)" value={vehicleImage} onChange={setVehicleImage} />
            </SimpleGrid>
          </Stack>
        </Stepper.Step>

        {/* Step 3 — documents */}
        <Stepper.Step label="Documents" allowStepSelect={false}>
          <Stack mt="md">
            <Alert icon={<IconInfoCircle size={16} />} color="gray">
              <b>Licence</b>, <b>LATRA D4</b> and <b>insurance</b> are required to send
              the driver for approval. Anything you fill in is saved either way — the
              driver stays in the onboarding queue until the required set is complete.
              The licence expiry is set when you approve the driver, not here.
            </Alert>
            <Text size="sm" fw={700} c="dimmed">Driving license</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput label="License number" value={licenseNumber} onChange={(e) => setLicenseNumber(e.currentTarget.value)} />
              <TextInput label="License category" placeholder="e.g. C, D" value={licenseCategory} onChange={(e) => setLicenseCategory(e.currentTarget.value)} />
              <UploadField label="License (front)" value={licenseFront} onChange={setLicenseFront} uploadType="document" />
            </SimpleGrid>
            <Text size="sm" fw={700} c="dimmed" mt="sm">Vehicle registration</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput label="Registration number" value={regNumber} onChange={(e) => setRegNumber(e.currentTarget.value)} />
            </SimpleGrid>
            <Text size="sm" fw={700} c="dimmed" mt="sm">Insurance & LATRA D4</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <DateInput label="Insurance expiry" value={insExpiry} onChange={(d) => setInsExpiry(parseBackendDate(d as Date | string | null))} clearable />
              <UploadField label="Insurance" value={insImage} onChange={setInsImage} uploadType="document" />
              <DateInput label="LATRA D4 expiry" value={d4Expiry} onChange={(d) => setD4Expiry(parseBackendDate(d as Date | string | null))} clearable />
              <UploadField label="LATRA D4 permit" value={d4Image} onChange={setD4Image} uploadType="document" />
            </SimpleGrid>
          </Stack>
        </Stepper.Step>
      </Stepper>

      <Group justify="space-between" mt="lg">
        <Button variant="default" onClick={() => (step === 0 ? onClose() : setStep(step - 1))} disabled={busy}>
          {step === 0 ? "Cancel" : "Back"}
        </Button>
        {step === 0 ? (
          <Button disabled={!otpVerified} onClick={() => setStep(1)}>
            Next
          </Button>
        ) : step === 1 ? (
          <Button disabled={!detailsValid} onClick={() => setStep(2)}>
            Next
          </Button>
        ) : (
          <Button loading={busy} onClick={submit}>
            Create driver
          </Button>
        )}
      </Group>
    </Modal>
  );
}
